// moduleRoutes.js — the routes an installed workspace module/app injected at
// RUNTIME (`/exams`, `/exams/admin/registrations`, `/manage/members`, …).
//
// These cannot live in any static list in the shell: they depend entirely on
// what a given workspace has installed. Every classifier that asks "is this one
// of ours?" has to consult this ledger instead of a hardcoded route table, or
// it will silently misjudge exactly the surfaces a workspace adds:
//
//   - the MWT router treated them as non-intranet, so an in-app click dropped
//     the `/w/<org>` prefix and the `?ws=` workspace handle
//   - `_isProjectCanvasPath` claimed any 2-segment path whose first segment
//     wasn't in a hardcoded reserved set, so `/manage/members` and
//     `/exams/documents` were permanently misread as `<owner>/<project>`
//     canvases — dragging canvas chrome (Zoombar, Inspect/Split/Sidebar
//     buttons, the full-bleed `data-shell-route="canvas"` layout) onto ordinary
//     app pages. That reserved list could never be right; it drifts the moment
//     anyone installs an app whose first segment isn't in it.
//
// Sourced from the ledgers the loaders already publish — `workspaceProject.pages`
// (takeover module) and `workspaceApps[].pages` (additive Manifest v2 apps) —
// so there is one source of truth and no second registry to keep in sync.
//
// Pure-ish module, direct ESM import (workspacePrefix.js / guestRoutes.js
// pattern) — not registered in functions/index.js, never frank-serialized.

// Root state → Set of injected route paths, or null when nothing is installed.
// Null (rather than an empty Set) so callers can cheaply skip the whole check.
export const moduleRoutesFromRoot = (root) => {
  try {
    if (!root) return null
    const set = new Set()
    const takeover = root.workspaceProject && root.workspaceProject.pages
    if (takeover) for (const p of Object.keys(takeover)) set.add(p)
    if (Array.isArray(root.workspaceApps)) {
      for (const app of root.workspaceApps) {
        if (Array.isArray(app?.pages)) for (const p of app.pages) set.add(p)
      }
    }
    return set.size ? set : null
  } catch {
    return null
  }
}

// Does `path` belong to an installed module/app?
//
// Matches the exact path, then falls back to its OWNING TOP SEGMENT — a module
// declares `/exams` and `/exams/admin/registrations`, but may also route to
// sub-paths it never declared, and the whole app must classify consistently or
// its pages disagree about their own chrome and URL shape.
export const isModuleRoute = (path, routes) => {
  if (!routes || typeof path !== 'string' || !path) return false
  const has = routes instanceof Set ? (k) => routes.has(k) : (k) => !!routes[k]
  const clean = path.split(/[?#]/)[0]
  if (has(clean)) return true
  const first = clean.split('/').filter(Boolean)[0]
  return !!first && has('/' + first)
}

// Is `route` a CANVAS page of an installed module/app?
//
// A module opts a page into the shell's canvas chrome — full-bleed under the
// floating header like /tasks, the root Zoombar driving the instance it
// registers (canvas/functions/registerCanvasPanzoom.js) — by listing it in its
// manifest's `canvasRoutes` (published on `root.workspaceApps[].canvasRoutes`
// and, for a takeover module, `root.workspaceProject.canvasRoutes`). Only a
// route the module itself serves counts (its `pages`), and never `/`, so no
// manifest can turn a shell surface into a canvas.
//
// `route` is a workspace-relative route (callers strip the `/w/<org>/…` prefix
// as they already do for their other classifiers). Exact path match after the
// query/hash — a canvas route's sub-paths are ordinary pages. Self-contained
// (FA204 / frank trap): no sibling calls, no module-scope consts.
export const isModuleCanvasRoute = (route, root) => {
  if (!root || typeof route !== 'string' || !route) return false
  const clean = route.split(/[?#]/)[0]
  if (!clean || clean === '/') return false
  try {
    const owns = (pages) => {
      if (Array.isArray(pages)) return pages.indexOf(clean) !== -1
      return !!(pages && typeof pages === 'object' && pages[clean])
    }
    const lists = (list) =>
      Array.isArray(list) && list.some((p) => typeof p === 'string' && p.split(/[?#]/)[0] === clean)
    const take = root.workspaceProject
    if (take && lists(take.canvasRoutes) && owns(take.pages)) return true
    const apps = Array.isArray(root.workspaceApps) ? root.workspaceApps : []
    return apps.some((app) => !!app && lists(app.canvasRoutes) && owns(app.pages))
  } catch {
    return false
  }
}
